import { Component, ChangeDetectionStrategy, signal, inject, effect, HostListener } from '@angular/core';
import { RouterOutlet, RouterLink, Router, ActivatedRoute, NavigationEnd } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter, map } from 'rxjs';
import { Navbar } from '../../common/navbar/navbar';
import { StaticSidemenu } from './static-sidemenu';

/**
 * Shell for the STATIC reference route family (`/static/*`). Mirrors the live
 * `Layout` (same navbar + mobile-drawer behaviour + `data.key` resolution) but
 * uses `StaticSidemenu` (links within `/static/*`) and shows a reference banner
 * so it's unmistakable you're viewing frozen, non-API pages.
 */
@Component({
  selector: 'app-static-layout',
  imports: [RouterOutlet, RouterLink, Navbar, StaticSidemenu],
  templateUrl: './static-layout.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StaticLayout {
  private router = inject(Router);
  private route = inject(ActivatedRoute);

  protected readonly sidebarOpen = signal(false);
  protected readonly activeKey = signal<string>('');

  constructor() {
    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        map(() => this.resolveKey()),
        takeUntilDestroyed(),
      )
      .subscribe((key) => {
        this.activeKey.set(key);
        this.sidebarOpen.set(false);
      });

    effect(() => {
      document.body.classList.toggle('sidebar-locked', this.sidebarOpen());
    });
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.closeSidebar();
  }

  toggleSidebar(): void {
    this.sidebarOpen.update((open) => !open);
  }

  closeSidebar(): void {
    this.sidebarOpen.set(false);
  }

  /** Walk to the deepest activated child route and read its `key` data. */
  private resolveKey(): string {
    let route = this.route.firstChild;
    let key = '';
    while (route) {
      const routeKey = route.snapshot.data['key'];
      if (routeKey) key = routeKey;
      route = route.firstChild;
    }
    return key;
  }
}
